// Balance sheet — what the desk owns, what it owes, and what is left over.
//
// THE QUESTION THIS ANSWERS: "what is the desk actually worth right now?"
// Profit answers a different one — what it earned over a period — and the two
// are kept apart deliberately (operator: "seperate from a profit screen").
//
// THE ONE RULE THE LAYOUT EXISTS TO MAKE VISIBLE: assets − liabilities = equity.
// If that does not hold, the sheet says so at the top in red rather than
// presenting a tidy set of numbers that happen not to add up. The check is the
// headline, not a footnote, because a balance sheet that cannot prove it closes
// is worse than no balance sheet: it looks authoritative either way.
//
// CURRENCY POLICY — and this screen is the ONE deliberate exception in the app.
// Everywhere else (Profit, Trading, Treasury) currencies never blend, because
// "inventing an FX rate to produce one number would be a fabricated profit
// figure". A balance sheet cannot honour that: "what is it worth" is a
// single-number question. The exception is safe only because the rate is not
// invented — it is the median of THIS DESK'S OWN USDT/AED and USDT/INR trades,
// crossed. So both readings are always on screen: every line carries its native
// per-asset amounts, and the AED column is clearly derived, with the rate and
// its sample size shown under the sheet rather than buried.
//
// "—" NEVER MEANS ZERO. Drawings and retained earnings render as em dashes
// because they have never been posted — there is no period close yet, so what
// the desk has earned still sits inside the trading position. A 0.00 there would
// claim the owner has taken nothing out, which is a statement about the business
// rather than about the books.
//
// LAYOUT: lg+ gets a statement table with the AED column on the right and native
// amounts beneath each label; below lg, one block per section — the Ledger's
// block header carrying the section and its total, then the SAME rows as the
// statement (lineRow, one builder for both forms) in a headless, unframed
// ReportTable, exactly as Profit's currency blocks stand on the phone.
//
// 14 Sept 2026 (SWEEP-SCOPE §5.24): "About this screen" holds the three
// permanent info notes (which kept "Notes (n)" from ever reaching zero) and
// the Rates / Stated valuations paragraphs; "Notes (n)" counts warnings only;
// Net worth is in the header once (the phone card under the sections is
// gone); the account counts are gone; the green proof is one line; the
// estimate line is short enough never to truncate; the phone totals are
// named as on desktop; the table and fmtRate are the shared pieces.
//
// 19 Sept 2026 (phone pass): the phone cards had a row shape of their own —
// truncating labels, a hand-drawn total line, the caveat missing — and are
// now the Profit-shaped blocks above; a position part whose label is longer
// than a phone is wide lets its figure drop to the next line instead of
// squeezing the words into a column beside it; a rate row on the phone is two
// lines — pair · figure, then the sample or source under it — so the cross
// rate's derivation and a stated price's source never wrap beside the number;
// the proof line and the rate rows read at 13px there, and the native amounts
// and position parts — figures, not hints — at 12px. Desktop is unchanged.
//
// 21 Oct 2026 audit (overnight clean view; operator: "I need clean view for
// operator"): a line the sheet could not value names WHICH asset on the line
// itself ("XRP not valued") and the could-not-be-proved bar names it too —
// they were a bare "—" and a name inside the folded Notes. The gap is said
// once, with its unit and figure: the red bar; the Worker's own copy of it is
// no longer pinned under it (it reads whole in About, and still prints where
// the bar does not say it). The dealt rate in the rate warning carries its
// currency. The all-clear is a small "Balances" pill beside the stamp, not a
// full-width green bar — the red bars are unchanged. A line that holds AED
// and nothing else prints its amount once (the AED column), not again under
// its name. The native amounts break between assets with a gap, not a dot —
// a wrap left a dot dangling at a line's end. The Rates card stands only when
// a line used a rate (or the stated prices could not be read).
//
// 21 Oct 2026 design-system sweep: the red proof bar ("Not proved to balance — XRP not
// valued; totals provisional." / "This sheet does not balance — off by 4,210.55 AED."),
// the pinned rate warning ("SOL at stated 537 AED, dealt 480 AED — 12% apart.") and the
// unreadable stated prices are one short line each — the rule, or the Worker's whole
// sentence, behind a tap or in About; a failed read keeps the title and is a red line.

import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { readWords } from '../lib/errors';
import { api } from '../lib/api';
import { ScreenHeader } from '../components/ScreenHeader';
import { Card, Pending, Pill, StateLine } from '../components/kit';
import { ReportTable, type ReportRow } from '../components/ReportTable';
import { LedgerHead } from '../components/Ledger';
import { WarnLine } from '../components/ErrorLine';
import type {
  BalanceSheetLine,
  BalanceSheetNote,
  BalanceSheetResponse,
  BalanceSheetSection,
} from '../lib/api';
import { displayAsset, fmtAge, fmtAmount, fmtDate, fmtDay, fmtRate, fmtTime } from '../lib/format';
import { valuationSourceName } from '../lib/naming';

/** Does a note name a figure — "differ by 1,234.00 AED"? Such a note is a
 *  number the operator may need and stays in view; the other warnings fold
 *  behind one "Notes (n)" line (7 Sept 2026). */
function namesAmount(message: string): boolean {
  return /\d[\d,]*(?:\.\d+)?\s*(?:AED|INR|USDT)\b|\b(?:AED|INR|USDT)\s*\d/.test(message);
}

/**
 * A LINE THAT IS PART ESTIMATE SAYS SO, ON THE LINE.
 *
 * Decision of 4 Sept 2026: a valued figure is always labelled, always dated, and
 * never mixed silently into a book figure. The AED beside this label is part
 * counted and part valued — the desk has never dealt these assets, so their
 * contribution comes from a price somebody stated on a named date. The date and
 * the source are in Rates below; the words are short enough to fit on a phone
 * without truncation (hover does not exist there).
 */
function Estimated({ assets }: { assets?: string[] }) {
  if (!assets || assets.length === 0) return null;
  return (
    <div className="mt-0.5 text-meta text-tg-hint whitespace-normal">
      Part estimate · {assets.map(displayAsset).join(', ')} at stated prices
    </div>
  );
}

/** WHAT THE SHEET COULD NOT VALUE, BY NAME (21 Oct 2026 audit). The Worker
 *  names the assets in its `unpriced_assets` note ("…no stated valuation for
 *  XRP — that balance is excluded…") and on a position part's `unpriced`. A
 *  line whose AED is "—" says which asset on the line, and the proof bar says
 *  it, instead of leaving a bare dash and a name inside the folded Notes. A
 *  miss on the sentence (its wording moved) names nobody: the line then says
 *  "Not valued" plain — never nothing. */
function unvaluedAssets(data: BalanceSheetResponse): string[] {
  const out = new Set<string>();
  for (const n of data.notes) {
    if (n.code !== 'unpriced_assets') continue;
    const m = /valuation for (.+?) — (?:that balance is|those balances are) excluded/.exec(n.message);
    for (const a of (m?.[1] ?? '').split(', ')) if (a.trim()) out.add(a.trim());
  }
  for (const sec of data.sections) for (const l of sec.lines) for (const p of l.parts ?? []) for (const a of p.unpriced ?? []) out.add(displayAsset(a));
  return [...out];
}

/** The assets of a posted line the sheet could not value, on the line — the
 *  same place "Part estimate" says what was valued from a stated price. */
function Unvalued({ line, unvalued }: { line: BalanceSheetLine; unvalued: string[] }) {
  if (!line.posted || line.aed !== null) return null;
  const named = Object.entries(line.amounts)
    .filter(([, v]) => v !== 0)
    .map(([a]) => displayAsset(a))
    .filter((a) => unvalued.includes(a));
  return (
    <div className="mt-0.5 text-meta text-warn whitespace-normal">
      {named.length ? `${named.join(', ')} not valued` : 'Not valued'}
    </div>
  );
}

/** A line that holds AED and nothing else prints its amount twice — under the
 *  label and in the AED column. It is said once, in the AED column (21 Oct
 *  2026 audit). Compared AS PRINTED, so a figure that differs keeps both. */
function echoesAed(amounts: Record<string, number>, aed: number | null): boolean {
  const entries = Object.entries(amounts).filter(([, v]) => v !== 0);
  const only = entries[0];
  if (entries.length !== 1 || !only || only[0] !== 'AED' || aed === null) return false;
  return fmtAmount(only[1], 'AED') === fmtAmount(aed, 'AED');
}

/** The reporting-currency figure. Absent is an em dash, never a zero — a blank
 *  cell and a real zero mean different things to someone reconciling. */
function Aed({ value, strong = false, signed = false }: {
  value: number | null; strong?: boolean; signed?: boolean;
}) {
  if (value === null) return <span className="text-tg-hint">—</span>;
  const colour = signed ? (value > 0 ? 'text-pos' : value < 0 ? 'text-neg' : '') : (value < 0 ? 'text-neg' : '');
  return (
    <span className={`font-mono tabular ${strong ? 'font-semibold' : ''} ${colour}`}>
      {fmtAmount(value, 'AED')}
    </span>
  );
}

/** The native per-asset amounts behind a line. This is the reading that is
 *  always true; the AED column beside it is the derived one.
 *  21 Oct 2026 audit: the assets are separated by a gap, not a " · " — a wrap
 *  fell beside the dot and left it hanging at the end of a line ("· 0.513 BTC
 *  · 0.0397 ETH ·"). */
function Native({ amounts }: { amounts: Record<string, number> }) {
  const entries = Object.entries(amounts).filter(([, v]) => v !== 0);
  if (entries.length === 0) return null;
  return (
    <span className="flex flex-wrap gap-x-3 text-meta text-tg-hint whitespace-normal">
      {entries.map(([asset, v]) => (
        // A figure and its code are one piece — a wrap falls between assets, never inside one.
        <span key={asset} className="whitespace-nowrap"><span className="font-mono tabular">{fmtAmount(v, asset)}</span> {displayAsset(asset)}</span>
      ))}
    </span>
  );
}

export function BalanceSheet() {
  const q = useQuery({
    queryKey: ['balance-sheet'],
    queryFn: () => api.balanceSheet(),
  });

  // Error first: writing the data guard first makes an errored query show
  // "Loading…" forever, since react-query v5 leaves data undefined on error.
  // While there is nothing to draw — reading, or a read that failed — the title
  // stays (sweep, 21 Oct 2026: the screen was a bare red line with no name), and
  // a failed read is a line of its own: what failed, the first sentence of what
  // the books said, the rest behind a tap, and the way out.
  if (q.isError) {
    const said = readWords(q.error);
    const first = said.split(/(?<=[.!?])\s+/)[0] ?? said;
    return (
      <div className="space-y-4">
        <ScreenHeader title="Balance sheet" />
        <StateLine
          tone="bad"
          more={said.slice(first.length).trim() || undefined}
          action={<button type="button" className="btn-link" onClick={() => q.refetch()}>Try again</button>}
        >
          Couldn&rsquo;t read the balance sheet. {first}
        </StateLine>
      </div>
    );
  }
  if (!q.data) return <div className="space-y-4"><ScreenHeader title="Balance sheet" /><Pending /></div>;

  const data = q.data;
  const [assets, liabilities, equity] = data.sections;

  // A WARNING THAT NAMES AN AMOUNT STAYS IN VIEW; every other warning folds
  // behind one "Notes (n)" line under the proof (7 Sept 2026). The grey info
  // notes are how the sheet works and live in About this screen — they used
  // to keep the count from ever reading zero. The red does-not-balance line
  // is not a note and never folds.
  const warnings = data.notes.filter((n) => n.tone === 'warn');
  const info     = data.notes.filter((n) => n.tone !== 'warn');
  const pinned = warnings.filter((n) => namesAmount(n.message));
  const folded = warnings.filter((n) => !namesAmount(n.message));
  // THE GAP IS SAID ONCE (21 Oct 2026 audit): the red bar already carries the
  // figure and its unit, so the Worker's own "differ by 4210.55 AED" copy of it
  // is not pinned under the bar as well. It still reads whole in About (below)
  // and still prints where the bar does NOT say it — a gap under a dirham,
  // which the check calls balanced.
  const barSaysGap = !data.check.closes && data.check.presentation_gap_aed !== null;
  const shownPinned = pinned.filter((n) => !(barSaysGap && n.code === 'presentation_gap'));
  // The lines whose caveat is a sentence — under the line it made a
  // three-line row (20 Sept 2026); it reads in About, named by its line.
  const caveats = data.sections.flatMap((sec) => sec.lines.filter((l) => l.caveat).map((l) => ({ key: l.key, label: l.label, caveat: l.caveat! })));
  // What could not be valued, by name — on the line and in the proof bar.
  const unvalued = unvaluedAssets(data);
  // A rate was used when a line holds anything but AED; with none, the Rates
  // card says nothing the sheet leaned on. An unreadable stated-price table is
  // a failed read and keeps the card (its own words are in it).
  const otherThanAed = (amounts: Record<string, number>) => Object.entries(amounts).some(([a, v]) => v !== 0 && a !== 'AED');
  // …a position's PARTS count too: parts that cancel in the parent still show their converted AED (Astra review, 21 Oct 2026).
  const usedRate = data.sections.some((s) => s.lines.some((l) => l.posted && (otherThanAed(l.amounts) || (l.parts ?? []).some((p) => otherThanAed(p.amounts)))));
  // …and a card with no row to show (the Worker named no pair, no peg and no
  // stated price) is a title over nothing, so it is not drawn either.
  const { rates } = data;
  const hasRateRows = rates.pairs.length > 0 || !!rates.cross || rates.pegged.length > 0 || (rates.stated ?? []).length > 0;

  return (
    <div className="space-y-4">
      <Header data={data} info={info} warnings={pinned} caveats={caveats} unvalued={unvalued} />

      {shownPinned.length > 0 && (
        <div className="space-y-1">
          {shownPinned.map((n) => <StateLine key={n.code + n.message.slice(0, 12)} tone="warn">{shortWarning(n)}</StateLine>)}
        </div>
      )}
      {folded.length > 0 && (
        <details className="rounded-lg border border-tg-secondary lg:border-stroke">
          <summary className="cursor-pointer px-3 py-1.5 text-meta text-tg-hint">
            Notes ({folded.length})
          </summary>
          <div className="space-y-0.5 border-t border-tg-secondary lg:border-stroke px-3 py-2">
            {folded.map((n) => <WarnLine key={n.code + n.message.slice(0, 12)} className="mt-0">{n.message}</WarnLine>)}
          </div>
        </details>
      )}

      {/* ── lg+: one statement ─────────────────────────────────────────────── */}
      <div className="hidden lg:block">
        <Statement data={data} unvalued={unvalued} />
      </div>

      {/* ── below lg: one block per section (Net worth is in the header) ─── */}
      <div className="lg:hidden">
        {[assets, liabilities, equity].filter(Boolean).map((s) => (
          <SectionBlock key={s!.key} section={s!} unvalued={unvalued} />
        ))}
      </div>

      {((usedRate && hasRateRows) || rates.stated_available === false) && <Rates data={data} onRetry={() => q.refetch()} />}
    </div>
  );
}

// ── header ──────────────────────────────────────────────────────────────────
/** A WARNING IS ONE SENTENCE — the fact and its figures (Profit's rule).
 *  The BFF's rate note carries the mechanism too ("The stated price wins
 *  because it is newer than…") and names its price source; that half reads
 *  in About, once. An unknown code keeps its first sentence. */
function shortWarning(n: BalanceSheetNote): string {
  if (n.code === 'rate_disagreement') {
    // The two figures are RATES (AED per unit), printed as the Rates block
    // prints them (fmtRate) — never through fmtAmount, which rounds AED to
    // the dirham and would call 3.67 and 3.60 both "4". The source name
    // is free text and may hold a full stop, so the sentence is matched
    // whole; a miss prints the note as it came.
    const m = /^(\S+) is valued at the (stated price|desk's own dealt rate) \((\d[\d.]*) AED[^)]*\), which is (\d+%) away from the ([^(]+?) \((\d[\d.]*) AED[^)]*\)\./.exec(n.message);
    if (!m) return n.message;
    // The sweep, 21 Oct 2026: ONE short line — "SOL at stated 537 AED, dealt 480 AED — 12% apart." (was 14 words).
    // The figure the sheet valued the asset at comes first ("at"); both rates carry their unit; the Worker's
    // whole sentence, with its source and date, reads in About.
    const stated = m[2] === 'stated price';
    const [first, second] = stated ? ['stated', 'dealt'] : ['dealt', 'stated'];
    return `${m[1]} at ${first} ${fmtRate(m[3], 'AED')} AED, ${second} ${fmtRate(m[6], 'AED')} AED — ${m[4]} apart.`;
  }
  return n.message.split(/(?<=\.)\s+/)[0] ?? n.message;
}

function Header({ data, info, warnings, caveats, unvalued }: {
  data: BalanceSheetResponse; info: BalanceSheetNote[]; warnings: BalanceSheetNote[];
  caveats: Array<{ key: string; label: string; caveat: string }>; unvalued: string[];
}) {
  const closes = data.check.closes;
  const gap = data.check.presentation_gap_aed;
  // The all-clear is a small word beside the stamp (21 Oct 2026 audit) — a
  // green full-width bar said "Balances." on every healthy day. The proof that
  // FAILS, or could not be run, is still the red bar below the title. And a
  // sheet the Worker itself flags with a gap (one under a dirham, which the
  // check calls balanced) is not told green: its amber line stands alone.
  const clear = closes && !data.notes.some((n) => n.code === 'presentation_gap');
  return (
    <ScreenHeader
      title="Balance sheet"
      subtitle={<>{fmtDay(data.as_of, true)}, {fmtTime(data.as_of)}{clear && <span className="ml-2"><Pill tone="good">Balances</Pill></span>}</>}
      actions={
        // On the phone this block sits on its own row under the title, at the
        // left, so it reads left-aligned there; at lg+ it is the right-hand
        // action it always was.
        <div className="text-left lg:text-right">
          <p className="text-label uppercase tracking-[0.06em] text-tg-hint">Net worth</p>
          <p className="text-title font-mono tabular font-semibold lg:text-figure">
            {data.net_assets_aed === null ? '—' : fmtAmount(data.net_assets_aed, 'AED')}
            <span className="ml-1.5 text-meta font-sans font-normal text-tg-hint">AED</span>
          </p>
        </div>
      }
      help={
        <>
          <p>
            What the desk owns, owes and has left over, at this instant. Every line carries its own
            currencies; the AED column beside it is derived.
          </p>
          {info.map((n) => <p key={n.code + n.message.slice(0, 12)}>{n.message}</p>)}
          {/* The pinned warnings in full — the screen prints one sentence of
              each; the folded ones already read whole under Notes. */}
          {warnings.map((n) => <p key={n.code + n.message.slice(0, 12)}>{n.message}</p>)}
          {caveats.map((c) => <p key={c.key}><span className="text-tg-text">{c.label}</span> — {c.caveat}</p>)}
          <p>
            Rates are derived from this desk&rsquo;s own trades — not a market feed. The median, so one
            mistyped rate cannot move it.
          </p>
          <p>
            Stated valuations are prices somebody named and dated, not ones this desk dealt. Every AED
            figure that leans on one is an estimate, and its line says so.
          </p>
        </>
      }
    >
      {/* The proof, not a footnote. `closes` is false when the sheet could not
          be CHECKED as well as when it fails — unverified is not a pass — and
          then it is this red bar, whole. A sheet that balances is the small
          word beside the stamp above. The unvalued asset is named here as it
          is on its line. */}
      {!closes && (
        // ONE line (the sweep, 21 Oct 2026: 16 and 13 words): that it is not proved or does not balance, the
        // asset it could not value or the gap with its unit, and that the totals are provisional; the rule
        // behind it is one tap down. Red and a bar, as before ("the red bars are unchanged", 21 Oct) — `closes`
        // is false when the sheet could not be CHECKED as well as when it fails.
        <Card tone="bad" className="bg-neg-bg">
          {gap === null ? (
            <StateLine tone="bad" more="A line the sheet could not value is left out of its AED figure, so assets, liabilities and equity cannot be proved equal.">
              Not proved to balance — {unvalued.length > 0 ? `${unvalued.join(', ')} not valued` : 'a line is unvalued'}; totals provisional.
            </StateLine>
          ) : (
            <StateLine tone="bad" more={`Assets − liabilities and equity differ by ${fmtAmount(gap, 'AED')} AED.`}>
              This sheet does not balance — off by {fmtAmount(gap, 'AED')} AED.
            </StateLine>
          )}
        </Card>
      )}
    </ScreenHeader>
  );
}

// ── desktop statement ───────────────────────────────────────────────────────
function lineName(line: BalanceSheetLine, unvalued: string[]) {
  return (
    <div className="whitespace-normal">
      <div className="flex flex-wrap items-baseline gap-x-2">
        <span className={line.posted ? '' : 'text-tg-hint'}>{line.label}</span>
        {!line.posted && <span className="text-meta text-tg-hint">never posted</span>}
      </div>
      {/* AED and nothing else: the AED column says it (printed twice before). */}
      {line.posted && !echoesAed(line.amounts, line.aed) && <Native amounts={line.amounts} />}
      {line.posted && <Estimated assets={line.stated} />}
      {line.posted && <Unvalued line={line} unvalued={unvalued} />}
      {/* The line's caveat reads in About, named by the line. */}
    </div>
  );
}

/** One line of the sheet as a report row — the name column with its native
 *  amounts, parts, estimate and caveat beneath the label; the AED column
 *  beside it. ONE builder for the desktop statement and the phone blocks, so
 *  the two forms can never disagree on a word, a weight or a figure. */
function lineRow(line: BalanceSheetLine, unvalued: string[]): ReportRow[] {
  const main: ReportRow = {
    key: line.key,
    cells: {
      line: lineName(line, unvalued),
      aed: <Aed value={line.posted ? line.aed : null} signed={line.key === 'equity.position'} />,
    },
  };
  // THE PARTS OF A LINE ARE ROWS OF THE TABLE (21 Sept 2026): the open
  // position's four parts used to sit inside the name column with their
  // figures mid-table, on no column at all, under a bare entry count with
  // the part's own name missing. Each part is a row now — its name, the
  // entry count and its native amounts indented under the line, its AED
  // in the AED column, muted, never re-added to any total. A part whose
  // AED is null holds an asset the desk has no rate for: a dash, and the
  // asset named.
  const parts: ReportRow[] = (line.parts ?? []).map((p) => ({
    key: `${line.key}:${p.key}`,
    kind: 'sub',
    cells: {
      line: (
        <div className="pl-4 whitespace-normal">
          {/* The part's name without its parenthetical — "(the open
              exposure)", "(passes through, nets out)" are the mechanism, on
              the title and in About. */}
          <span className="text-meta text-tg-hint" title={p.label}>{p.label.replace(/\s*\([^)]*\)/g, '')}</span>
          <span className="ml-1.5 text-meta text-tg-hint opacity-70">{p.txns} {p.txns === 1 ? 'entry' : 'entries'}</span>
          {!echoesAed(p.amounts, p.aed) && <Native amounts={p.amounts} />}
          {p.unpriced?.length ? (
            // The same words as the line above it (21 Oct 2026 audit).
            <div className="text-meta text-warn">
              {p.unpriced.map(displayAsset).join(', ')} not valued
            </div>
          ) : null}
        </div>
      ),
      aed: p.aed === null
        ? <span className="text-tg-hint">—</span>
        : <span className="font-mono tabular text-meta text-tg-hint">{fmtAmount(p.aed, 'AED')}</span>,
    },
  }));
  return [main, ...parts];
}

function Statement({ data, unvalued }: { data: BalanceSheetResponse; unvalued: string[] }) {
  const rows: ReportRow[] = data.sections.flatMap((section) => [
    { key: `g-${section.key}`, kind: 'group' as const, cells: { line: section.label } },
    ...section.lines.flatMap((l) => lineRow(l, unvalued)),
    {
      key: `t-${section.key}`, kind: 'total' as const,
      cells: { line: `Total ${section.label.toLowerCase()}`, aed: <Aed value={section.total_aed} strong /> },
    },
  ]);
  rows.push({
    key: 'net-worth', kind: 'total',
    cells: {
      line: <>Net worth<span className="ml-2 text-meta font-normal text-tg-hint">assets − liabilities</span></>,
      aed: <Aed value={data.net_assets_aed} strong />,
    },
  });
  return (
    <ReportTable
      columns={[
        // The name column wraps: a line name with its caveat is a sentence,
        // and a sentence that cannot wrap pushes the AED figure off the page.
        { key: 'line', label: '', align: 'left', width: '62%', nowrap: false },
        { key: 'aed', label: 'AED' },
      ]}
      rows={rows}
    />
  );
}

// ── phone block ─────────────────────────────────────────────────────────────
//
// The section block the phone reads, in the shape of Profit's currency
// blocks: the Ledger's block header — the section at the left, "total" and
// its figure at the right, so the answer is on the first line of every block
// as the eye scrolls — then the statement's own rows (lineRow), two columns
// wide: the line and its AED. Headless and unframed: the header line is the
// LedgerHead's, and the block stands flat under it as every currency block
// on Trades, Money and Profit does. The head takes the table's own cell inset
// (px-4) so the section stands over the line words and the total over the
// figures, on one x. The line column wraps rather than scrolls — a line name
// with its native amounts and parts is longer than a phone is wide once the
// figure has its seat. A section with no lines (nothing posted on that side
// yet) is its head alone, as on the desktop it is a group row and a total.
function SectionBlock({ section, unvalued }: { section: BalanceSheetSection; unvalued: string[] }) {
  return (
    <section className="pt-3 pb-1">
      <LedgerHead
        className="px-4"
        title={section.label}
        figure={<Aed value={section.total_aed} strong />}
        note="total"
        noteFirst
      />
      {section.lines.length > 0 && (
        <ReportTable
          head={false}
          frame={false}
          columns={[
            { key: 'line', label: '', align: 'left', nowrap: false },
            { key: 'aed', label: 'AED' },
          ]}
          rows={section.lines.flatMap((l) => lineRow(l, unvalued))}
        />
      )}
    </section>
  );
}

// ── rates ───────────────────────────────────────────────────────────────────
//
// Shown BELOW the sheet but never hidden: the AED column is only as good as
// these, so the sample size and the spread travel with the number. The words
// about where they come from are in About this screen.

/** The phone form of a rate row's note: its own full-width line under the
 *  pair and the figure. (Tailwind's max-lg: variants, so lg+ is untouched.) */
const NOTE_PHONE = 'max-lg:order-3 max-lg:basis-full max-lg:ml-0 max-lg:mt-0.5 max-lg:text-meta';

/**
 * ONE RATE ROW: the pair, a muted note (the sample and spread, a derivation,
 * a basis, a source and date) and the figure.
 *
 * lg+ is exactly the row it always was — the note beside the pair
 * (`noteAt="pair"`: "INR/AED  median USDT/INR ÷ …") or beside the figure
 * (`noteAt="figure"`: "1,234 trades · 3.66–3.68  3.67"). On a phone neither
 * fits: the cross rate's derivation and a stated price's source ran to three
 * lines in a narrow column beside the number. There the row is two lines —
 * pair · figure, then the note under them. The two wrapper spans go
 * `contents` below lg so the three pieces become the row's own flex items
 * and take their phone order; the desktop DOM is unchanged.
 */
function RateRow({ pair, note, figure, noteAt = 'pair', className = '' }: {
  pair: string;
  note: ReactNode;
  figure: ReactNode;
  noteAt?: 'pair' | 'figure';
  className?: string;
}) {
  return (
    <div className={`flex flex-wrap items-baseline justify-between gap-x-3 text-body lg:text-meta ${className}`}>
      <span className="text-tg-hint max-lg:contents">
        <span>{pair}</span>
        {noteAt === 'pair' && note != null && <span className={`ml-1.5 text-meta opacity-70 ${NOTE_PHONE}`}>{note}</span>}
      </span>
      <span className="flex items-baseline gap-2 max-lg:contents">
        {noteAt === 'figure' && note != null && <span className={`text-meta text-tg-hint ${NOTE_PHONE}`}>{note}</span>}
        <span className="font-mono tabular max-lg:order-2">{figure}</span>
      </span>
    </div>
  );
}

function Rates({ data, onRetry }: { data: BalanceSheetResponse; onRetry: () => void }) {
  const { rates } = data;
  return (
    <section className="card lg:rounded-xl lg:border lg:border-stroke lg:bg-panel">
      <header className="mb-2">
        <h2 className="font-semibold text-body">Rates</h2>
      </header>

      <div className="space-y-1">
        {rates.pairs.map((p) => (
          <RateRow
            key={p.pair}
            pair={p.pair}
            noteAt="figure"
            note={<>{p.trades} {p.trades === 1 ? 'trade' : 'trades'} · {fmtRate(p.low, p.pair.split('/')[1])}–{fmtRate(p.high, p.pair.split('/')[1])}</>}
            figure={fmtRate(p.median, p.pair.split('/')[1])}
          />
        ))}

        {rates.cross && (
          <RateRow
            className="border-t border-tg-secondary lg:border-stroke-soft mt-1.5 pt-1.5"
            pair={rates.cross.pair}
            note={rates.cross.derivation}
            figure={rates.cross.inr_per_aed_display}
          />
        )}

        {rates.pegged.map((p) => (
          <RateRow key={p.pair} pair={p.pair} note={p.basis} figure={fmtRate(p.rate, p.pair.split('/')[1])} />
        ))}
      </div>

      <Stated rates={rates} onRetry={onRetry} />
    </section>
  );
}

/**
 * STATED VALUATIONS — the prices this desk did not deal.
 *
 * The sheet's whole licence to print one AED number is that the rate is not
 * invented: it is the median of the desk's own trades. Assets the desk has
 * never traded come from a stated price with a named source and the date it
 * was true. That is still not invented, but it is asserted rather than dealt,
 * and the difference has to be legible or the licence does not hold — so each
 * one is listed with where it came from, when it was true and how old that
 * makes it.
 *
 * An empty list and an unreadable table are NOT the same thing and never
 * render the same way — the second is a failed read, and a failed read that
 * looks like "no estimates were used" is the exact shape of a lie.
 */
function Stated({ rates, onRetry }: { rates: BalanceSheetResponse['rates']; onRetry: () => void }) {
  const unreadable = rates.stated_available === false;
  const rows = rates.stated ?? [];
  if (!unreadable && rows.length === 0) return null;
  // One source, one reading, for every price — said once on the heading,
  // not eight times down the rows (21 Sept 2026). Rows from different
  // sources or readings keep their own words.
  const first = rows[0];
  const shared = !!first && rows.length > 1 && rows.every((s) => s.source === first.source && s.as_of === first.as_of);
  const provenance = (s: (typeof rows)[number]) => <>{valuationSourceName(s.source)} · {fmtDate(s.as_of)}{fmtAge(s.as_of) && ` · ${fmtAge(s.as_of)}`}</>;
  return (
    <div className="mt-3 border-t border-tg-secondary lg:border-stroke-soft pt-2.5">
      <h3 className="text-body font-semibold">
        Stated valuations
        {shared && first && <span className="ml-2 text-meta font-normal text-tg-hint">{provenance(first)}</span>}
      </h3>

      {unreadable ? (
        // A failed read is never a quiet day: one line, the reasoning behind the tap (the sweep, 21 Oct 2026).
        <StateLine tone="warn" className="mt-2" more="A failed read, not a settled fact about those holdings." action={<button type="button" className="btn-link" onClick={onRetry}>Try again</button>}>
          Couldn&rsquo;t read stated prices — those assets show no AED figure.
        </StateLine>
      ) : (
        <div className="mt-1.5 space-y-1">
          {rows.map((s) => (
            <RateRow
              key={`${s.asset}|${s.quote}`}
              pair={`${displayAsset(s.asset)}/${s.quote}`}
              note={shared ? null : provenance(s)}
              figure={fmtRate(s.rate, s.quote)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
